import axios from 'axios';
import config from './config.json';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: config.apiUrl,
  headers: config.headers,
  timeout: 10000
});

// Request interceptor to add headers
apiClient.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

class MainService {
  // Get latest products for home page
  async getLatestProducts() {
    try {
      const response = await apiClient.get(config.endpoints.latestProducts);
      return response.data;
    } catch (error) {
      console.error('Error fetching latest products:', error);
      throw error;
    }
  }

  // Get random/featured products for home page
  async getRandomProducts() {
    try {
      const response = await apiClient.get(config.endpoints.randomProducts);
      return response.data;
    } catch (error) {
      console.error('Error fetching random products:', error);
      throw error;
    }
  }

  async TopSellingProducts() {
    try {
      const response = await apiClient.get(config.endpoints.topSellingProducts);
      return response.data;
    } catch (error) {
      console.error('Error fetching Top Selling products:', error);
      throw error;
    }
  }

  async CatsProducts() {
    try {
      const response = await apiClient.get(config.endpoints.catsProducts);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async categoriesWithProducts () {
    try {
      const response = await apiClient.get(config.endpoints.categoriesWithProducts);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async categoriesInDropdown () {
    try {
      const response = await apiClient.get(config.endpoints.categoriesInDropdown);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async categoriesWithSubs() {
    try {
      const response = await apiClient.get(config.endpoints.categoriesWithSubs);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories with subcategories:', error);
      throw error;
    }
  }

  async getProductById(id) {
    try {
      const response = await apiClient.get(`${config.endpoints.getProductById}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  }

  async getSimilarProductsById(id) {
    try {
      const response = await apiClient.get(`${config.endpoints.getSimilarProductsById}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  }

  async addEnquiry(data) {
    try {
      const response = await apiClient.post(config.endpoints.addEnquiry, data);
      return response.data;
    } catch (error) {
      console.error('Error fetching Contact details:', error);
      throw error;
    }
  }

  async registerCustomer(data) {
    try {
      const response = await apiClient.post(config.endpoints.registerCustomer, data);
      return response.data;
    } catch (error) {
      console.error('Error fetching register:', error);
      throw error;
    }
  }

  async login(data) {
    try {
      const response = await apiClient.post(config.endpoints.login, data);
      return response.data;
    } catch (error) {
      console.error('Error fetching login:', error);
      throw error;
    }
  }

  async addWishlist() {
    try {
      const response = await apiClient.get(config.endpoints.addWishlist);
      return response.data;
    } catch (error) {
      console.error('Error fetching Wishlist details:', error);
      throw error;
    }
  }

  async GetWishlistByCusId(customerId) {
    try {
      const response = await apiClient.get(`${config.endpoints.GetWishlistByCusId}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching wishlist by customer ID:', error);
      throw error;
    }
  }

  // Alias for consistency
  async getWishlistByCusId(customerId) {
    return this.GetWishlistByCusId(customerId);
  }

  async RemoveWishlist(customerId) {
    try {
      const response = await apiClient.get(`${config.endpoints.RemoveWishlist}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching wishlist by customer ID:', error);
      throw error;
    }
  }

  async getTotalWishlist(customerId) {
    try {
      const response = await apiClient.get(`${config.endpoints.getTotalWishlist}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching total wishlist count:', error);
      throw error;
    }
  }

  async MyBookings(customerId) {
    try {
      const response = await apiClient.get(`${config.endpoints.MyBookings}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching my bookings:', error);
      throw error;
    }
  }

  async GetCustomerById(customerId) {
    try {
      const response = await apiClient.get(`${config.endpoints.GetCustomerById}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      throw error;
    }
  }

  async UpdateProfile(data) {
    try {
      const response = await apiClient.post(config.endpoints.UpdateProfile, data);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  async GetCustomerAddresses(customerId) {
    try {
      const response = await apiClient.get(`${config.endpoints.GetCustomerAddresses}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer addresses:', error);
      throw error;
    }
  }

  async EditAddress(data) {
    try {
      const response = await apiClient.post(config.endpoints.EditAddress, data);
      return response.data;
    } catch (error) {
      console.error('Error editing address:', error);
      throw error;
    }
  }

  async addAddress(data) {
    try {
      const response = await apiClient.post(config.endpoints.addAddress, data);
      return response.data;
    } catch (error) {
      console.error('Error adding address:', error);
      throw error;
    }
  }

  async Addnewaddress(data) {
    try {
      console.log('Addnewaddress called with data:', data);
      console.log('Endpoint:', config.endpoints.Addnewaddress);
      console.log('Full URL:', config.apiUrl + config.endpoints.Addnewaddress);
      
      const response = await apiClient.post(config.endpoints.Addnewaddress, data);
      console.log('Addnewaddress response:', response);
      return response.data;
    } catch (error) {
      console.error('Error adding address:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  }
  
  // Generic GET request
  async get(endpoint) {
    try {
      const response = await apiClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error(`Error fetching from ${endpoint}:`, error);
      throw error;
    }
  }

  // Generic POST request
  async post(endpoint, data) {
    try {
      const response = await apiClient.post(endpoint, data);
      return response.data;
    } catch (error) {
      console.error(`Error posting to ${endpoint}:`, error);
      throw error;
    }
  }

  // Generic PUT request
  async put(endpoint, data) {
    try {
      const response = await apiClient.put(endpoint, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${endpoint}:`, error);
      throw error;
    }
  }

  // Generic DELETE request
  async delete(endpoint) {
    try {
      const response = await apiClient.delete(endpoint);
      return response.data;
    } catch (error) {
      console.error(`Error deleting ${endpoint}:`, error);
      throw error;
    }
  }

  // Areas and Locations APIs (using different base URL)
  async getAreasInDropDown() {
    try {
      const response = await axios.get('https://api-plus.ainsoftdelivery.com/EmiratesInDropDown');
      return response.data;
    } catch (error) {
      console.error('Error fetching areas:', error);
      throw error;
    }
  }

  async getLocationsByEid(areaId) {
    try {
      const response = await axios.get(`https://api-plus.ainsoftdelivery.com/GetLocationsByEid/${areaId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  async getDeliveryChargeOnline(data) {
    try {
      const response = await axios.post('https://api-plus.ainsoftdelivery.com/GetDeliveryChargeOnline', data);
      return response.data;
    } catch (error) {
      console.error('Error fetching delivery charge:', error);
      throw error;
    }
  }

  // Stripe Payment Intent
  async createPaymentIntent(data) {
    try {
      const response = await apiClient.post('CreatePaymentIntent', data);
      return response.data;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const mainService = new MainService();
export default mainService;
